/**
 * Students PA quotation API — live quotations-api when mock is off.
 */

import axios from 'axios';
import { normalizePaPaymentReference } from '@/utils/paPaymentReference';
import { normalizeNationalId } from '@/utils/paValidation';
import {
  isAgentPaMockEnabled,
  mockCreatePaQuotation,
  mockGetPaQuotation,
  mockGetPaRateCard,
  mockIssuePaPolicy,
  mockPaPayment,
  mockRecalculatePaPremium,
  mockUpdatePaQuotation,
} from './agentPaMock';

export { isAgentPaMockEnabled };

const PA_BASE = '/customer/quotation/pa';
const PAYMENT_BASE = '/customer/payment/client-api/v1/payments/mpesa';
const MSG_LOAD_PA_QUOTES = 'Failed to load PA quotes';
// Prevent the global axios interceptor from showing duplicate toasts on PA flows.
// Callers already handle user-facing errors (or return `success:false`).
const PA_SILENT_ERROR_CONFIG = {
  _skipErrorRedirect: true,
  _showErrorToast: false,
};

function paymentMpesaPath(channel, resource) {
  const agentSuffix = channel === 'agent' ? '/agent' : '';
  return `${PAYMENT_BASE}/${resource}${agentSuffix}`;
}

function unwrap(response) {
  const body = response?.data;
  if (body && typeof body.success === 'boolean') {
    return body;
  }
  return { success: true, data: body };
}

function fail(err, fallback = 'Request failed') {
  const msg = err?.response?.data?.msg || err?.response?.data?.message || err?.message || fallback;
  return { success: false, message: msg };
}

export async function getPaRateCard(productCode) {
  if (isAgentPaMockEnabled()) {
    return mockGetPaRateCard(productCode);
  }
  try {
    const res = await axios.get(`${PA_BASE}/rate-card`, {
      params: productCode ? { productCode } : {},
      ...PA_SILENT_ERROR_CONFIG,
    });
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Failed to load rate card');
  }
}

export async function createPaQuotation(payload) {
  if (isAgentPaMockEnabled()) {
    return mockCreatePaQuotation(payload);
  }
  try {
    const res = await axios.post(`${PA_BASE}/create`, payload, PA_SILENT_ERROR_CONFIG);
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Failed to create quote');
  }
}

export async function updatePaQuotation(quoteId, payload) {
  if (isAgentPaMockEnabled()) {
    return mockUpdatePaQuotation(quoteId, payload);
  }
  try {
    const body = { ...payload, quoteId: Number(quoteId) || quoteId };
    const res = await axios.put(`${PA_BASE}/update/agent`, body, PA_SILENT_ERROR_CONFIG);
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Failed to update quote');
  }
}

export async function recalculatePaPremium(optionCode, courseDuration, quoteId) {
  if (isAgentPaMockEnabled()) {
    return mockRecalculatePaPremium(optionCode, courseDuration);
  }
  try {
    const res = await axios.post(`${PA_BASE}/recalculate`, {
      optionCode,
      courseDuration,
      quoteId: quoteId != null ? Number(quoteId) : undefined,
    }, PA_SILENT_ERROR_CONFIG);
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Failed to recalculate premium');
  }
}

export async function getPaQuotation(quoteId, channel = 'customer') {
  if (isAgentPaMockEnabled()) {
    return mockGetPaQuotation(quoteId);
  }
  try {
    const path = channel === 'agent' ? `${PA_BASE}/agent` : `${PA_BASE}/quote`;
    const res = await axios.get(path, { params: { quoteId }, ...PA_SILENT_ERROR_CONFIG });
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Quote not found');
  }
}

/** True when quote is still an unpaid / in-progress application (Activity resume). */
export function isPaOpenQuote(quote) {
  if (!quote?.quoteId) return false;
  const policyNo = String(quote.policyNumber || '').trim();
  const paid = quote.paymentStatus === true || quote.paymentStatus === 1;
  if (paid && policyNo) return false;
  if (quote.status === 'POLICY_ISSUED' && policyNo) return false;
  return true;
}

export async function listPaClientPolicies({ nationalId, email } = {}) {
  if (isAgentPaMockEnabled()) {
    return { success: true, data: [] };
  }
  const id = normalizeNationalId(nationalId);
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (!id && !normalizedEmail) {
    return { success: false, message: 'National ID or email is required' };
  }
  try {
    const params = {};
    if (id) params.nationalId = id;
    if (normalizedEmail) params.email = normalizedEmail;
    const res = await axios.get(`${PA_BASE}/policies/client`, {
      params,
      _skipErrorRedirect: true,
      _showErrorToast: false,
    });
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Failed to load PA policies');
  }
}

/** Drop issued-policy rows when an older API ignores {@code view=open}. */
function unwrapOpenPaQuotes(response) {
  const body = unwrap(response);
  if (!body.success) {
    return body;
  }
  const rows = Array.isArray(body.data) ? body.data : [];
  const open = rows.filter((row) => isPaOpenQuote(row));
  return { ...body, data: open };
}

export async function listPaClientQuotes({ nationalId, email } = {}) {
  if (isAgentPaMockEnabled()) {
    const pending = typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem('pacis:pa:quote:pending')
      : null;
    if (pending) {
      try {
        const q = JSON.parse(pending);
        return q?.quoteId ? { success: true, data: [q] } : { success: true, data: [] };
      } catch {
        return { success: true, data: [] };
      }
    }
    return { success: true, data: [] };
  }
  const id = normalizeNationalId(nationalId);
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (!id && !normalizedEmail) {
    return { success: false, message: 'National ID or email is required' };
  }
  const params = { view: 'open' };
  if (id) params.nationalId = id;
  if (normalizedEmail) params.email = normalizedEmail;
  const requestConfig = {
    params,
    ...PA_SILENT_ERROR_CONFIG,
  };

  try {
    const res = await axios.get(`${PA_BASE}/policies/client`, requestConfig);
    return unwrapOpenPaQuotes(res);
  } catch (e) {
    const status = e?.response?.status;
    if (status === 404) {
      try {
        const legacy = await axios.get(`${PA_BASE}/quotes/client`, requestConfig);
        return unwrapOpenPaQuotes(legacy);
      } catch (legacyErr) {
        if (legacyErr?.response?.status === 404) {
          return { success: true, data: [] };
        }
        return fail(legacyErr, MSG_LOAD_PA_QUOTES);
      }
    }
    return fail(e, MSG_LOAD_PA_QUOTES);
  }
}

export async function listPaAgentQuotes(agentId, page = 0, size = 20) {
  try {
    const res = await axios.get(`${PA_BASE}/agent/quotes/${agentId}`, {
      params: { page, size },
      ...PA_SILENT_ERROR_CONFIG,
    });
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Failed to load PA quotes');
  }
}

export async function initPaPolicy(quoteId) {
  if (isAgentPaMockEnabled()) {
    return { success: true, data: { quoteId, readyForPayment: true } };
  }
  try {
    const res = await axios.post(
      `${PA_BASE}/policy/agent`,
      { quoteId: Number(quoteId) },
      PA_SILENT_ERROR_CONFIG,
    );
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Failed to initialise policy');
  }
}

function normalizeMpesaPhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length === 10) return `254${digits.slice(1)}`;
  if (digits.startsWith('254')) return digits;
  if (digits.startsWith('7') && digits.length === 9) return `254${digits}`;
  return digits;
}

/**
 * M-Pesa STK for Students PA (settlementProduct routes to PA payment queue).
 */
export async function requestPaStkPush({
  quoteId,
  payableAmount,
  customerPhoneNumber,
  transactionReference,
  channel = 'customer',
  agentId,
  kraPin,
}) {
  if (isAgentPaMockEnabled()) {
    return mockPaPayment(quoteId, 'mpesa');
  }
  const phone = normalizeMpesaPhone(customerPhoneNumber);
  if (phone.length < 12) {
    return { success: false, message: 'Enter a valid M-Pesa phone number' };
  }
  const ref = normalizePaPaymentReference(transactionReference);
  if (!ref) {
    return { success: false, message: 'Payment reference is required (PA-{national ID})' };
  }
  const desc = `PA ${quoteId}`.slice(0, 13);
  const payload = {
    serviceType: 'PORTAL',
    settlementProduct: 'PA_STUDENTS',
    quoteId: Number(quoteId),
    transactionReference: ref,
    transactionDescription: desc,
    payableAmount: Math.max(1, Math.round(Number(payableAmount) || 0)),
    customerPhoneNumber: phone,
  };
  if (channel === 'agent' || (agentId != null && Number(agentId) > 0)) {
    payload.transactionType = 'PAYMENT';
    payload.agentId = agentId != null ? Number(agentId) : 0;
    if (kraPin) payload.kraPin = kraPin;
  }
  try {
    const res = await axios.post(
      paymentMpesaPath(channel, 'stk/request'),
      payload,
      PA_SILENT_ERROR_CONFIG,
    );
    const body = res?.data;
    if (body?.success && body?.data?.paymentId) {
      return { success: true, data: { paymentId: body.data.paymentId } };
    }
    return { success: false, message: body?.message || 'Failed to trigger M-Pesa' };
  } catch (e) {
    return fail(e, 'M-Pesa STK failed');
  }
}

/**
 * Paybill — GIS validate + enqueue PA payment queue (issues policy via same path as STK).
 */
export async function confirmPaPaybillPayment({ quoteId, accountNumber }) {
  if (isAgentPaMockEnabled()) {
    return { success: true, message: 'Payment confirmed (mock)' };
  }
  const ref = normalizePaPaymentReference(accountNumber);
  if (!ref) {
    return { success: false, message: 'Payment reference is required (PA-{national ID})' };
  }
  try {
    const res = await axios.post(
      `${PA_BASE}/payment/paybill-confirm`,
      {
        quoteId: Number(quoteId),
        accountNumber: ref,
      },
      PA_SILENT_ERROR_CONFIG,
    );
    const body = res?.data;
    if (body?.success) {
      const msg = typeof body.msg === 'string' ? body.msg.trim() : '';
      const data = typeof body.data === 'string' ? body.data.trim() : '';
      const message = msg && data && msg !== data
        ? `${msg}. ${data}`
        : (msg || data || 'Payment confirmed. Your policy will be issued shortly.');
      const alreadyPaid = /already paid/i.test(msg) || /already applied/i.test(data);
      return { success: true, message, alreadyPaid };
    }
    return {
      success: false,
      message: body?.msg || body?.message || 'Payment not yet received. Complete Paybill and try again.',
    };
  } catch (e) {
    return fail(e, 'Payment confirmation failed');
  }
}

export async function pollPaPaymentStatus(paymentId, channel = 'customer') {
  if (isAgentPaMockEnabled()) {
    return { success: true, data: { paymentStatus: 'SUCCESSFUL' } };
  }
  try {
    const res = await axios.get(
      paymentMpesaPath(channel, 'payment/query'),
      { params: { paymentId }, ...PA_SILENT_ERROR_CONFIG },
    );
    const body = res?.data;
    if (body?.success) {
      return { success: true, data: { paymentStatus: body.data?.paymentStatus } };
    }
    return { success: false, message: body?.message || 'Could not check payment' };
  } catch (e) {
    return fail(e, 'Payment status check failed');
  }
}

/** @deprecated Use requestPaStkPush + pollPaPaymentStatus */
export async function processPaPayment(quoteId, paymentMethod) {
  if (isAgentPaMockEnabled()) {
    return mockPaPayment(quoteId, paymentMethod);
  }
  return { success: false, message: 'Use requestPaStkPush for live PA payment' };
}

/** Policy is issued server-side after payment; mock only. */
export async function issuePaPolicy(quoteId) {
  if (isAgentPaMockEnabled()) {
    return mockIssuePaPolicy(quoteId);
  }
  return { success: true, data: { quoteId, status: 'PAYMENT_RECEIVED' } };
}

/** IPRS "M/D/YYYY HH:MM:SS AM" → "YYYY-MM-DD" (no timezone shift). */
function parseIprsDob(value) {
  if (!value || value === 'null') return '';
  try {
    const [month, day, year] = String(value).split(' ')[0].split('/');
    if (month && day && year) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  } catch { /* ignore */ }
  return '';
}

/**
 * Verify identity via IPRS and fetch KRA PIN. Calls run independently.
 * @param {string} idNumber National ID
 * @param {'agent'|'customer'} [channel='agent'] — customer uses client-kra; agent uses kra-pin-check
 */
export async function verifyPaIdentity(idNumber, channel = 'agent') {
  if (isAgentPaMockEnabled()) {
    return {
      iprsUnavailable: false,
      iprs: {
        firstName: 'JOHN',
        fullName: 'JOHN DOE TEST',
        gender: 'M',
        citizenship: 'KENYAN',
        occupation: 'STUDENT',
        dateOfBirth: '2000-01-01',
      },
      kraVerified: true,
      kraPin: 'A012345678Z',
    };
  }

  const kraPath = channel === 'customer'
    ? '/customer/gis/policies/client-kra'
    : '/customer/gis/policies/kra-pin-check';

  const [iprsRes, kraRes] = await Promise.allSettled([
    axios.get('/customer/gis/policies/iprsdetail', {
      params: { idNumber },
      timeout: 15000,
      _skipErrorRedirect: true,
      _showErrorToast: false,
      validateStatus: () => true,
    }),
    axios.get(kraPath, {
      params: { idNumber },
      timeout: 15000,
      _skipErrorRedirect: true,
      _showErrorToast: false,
      validateStatus: () => true,
    }),
  ]);

  const out = {
    iprsUnavailable: false,
    iprs: null,
    kraVerified: false,
    kraPin: '',
    kraTaxpayerName: '',
  };
  const clean = (v) => (v && v !== 'null' ? v : '');

  if (iprsRes.status === 'fulfilled' && iprsRes.value.status < 500) {
    const raw = iprsRes.value.data?.object;
    const data = Array.isArray(raw) ? raw[0] : raw;
    if (data) {
      out.iprs = {
        firstName: clean(data.firstName).toUpperCase(),
        fullName: [data.firstName, data.otherName, data.surname]
          .filter((v) => v && v !== 'null')
          .join(' ')
          .trim(),
        gender: clean(data.gender),
        citizenship: clean(data.citizenShip),
        occupation: clean(data.occupation),
        dateOfBirth: parseIprsDob(data.dateOfBirth),
      };
    } else {
      out.iprsUnavailable = true;
    }
  } else {
    out.iprsUnavailable = true;
  }

  if (kraRes.status === 'fulfilled') {
    const kra = kraRes.value?.data?.object;
    if (kra && kra.ResponseCode === '30000') {
      out.kraVerified = true;
      out.kraPin = kra.TaxpayerPIN || '';
      out.kraTaxpayerName = kra.TaxpayerName || '';
    }
  }

  return out;
}

/** Resolve GIS agent short description for PA quotation / policy attribution. */
export async function resolveAgentShortCode(user) {
  const email = user?.email || '';
  const agnCode = user?.['custom:agent_code'];
  if (!agnCode && !email) return 'DIRECT';
  try {
    const baseUrl = '/customer/gis/agent/dashboard/summary';
    const url = agnCode
      ? `${baseUrl}?agn_code=${agnCode}`
      : `${baseUrl}?email=${encodeURIComponent(email)}`;
    const res = await axios.get(url, {
      validateStatus: () => true,
      timeout: 10000,
      _skipErrorRedirect: true,
    });
    if (res.status === 200 && res.data) {
      const shortDesc = res.data.shortDesc
        || res.data.agent?.shortDesc
        || res.data.summary?.shortDesc;
      if (shortDesc) return String(shortDesc);
    }
  } catch (e) {
    // fall through
  }
  return agnCode ? String(agnCode) : 'DIRECT';
}

/** Resolve GIS agent numeric code for STK / quotation attribution. */
export async function resolveAgentNumericId(user) {
  const email = user?.email || '';
  const agnCode = user?.['custom:agent_code'];
  if (!agnCode && !email) return 0;
  try {
    const baseUrl = '/customer/gis/agent/dashboard/summary';
    const url = agnCode
      ? `${baseUrl}?agn_code=${agnCode}`
      : `${baseUrl}?email=${encodeURIComponent(email)}`;
    const res = await axios.get(url, { validateStatus: () => true, _skipErrorRedirect: true });
    if (res.status === 200 && res.data?.agent?.code != null) {
      const parsed = parseInt(String(res.data.agent.code), 10);
      if (!Number.isNaN(parsed)) return parsed;
    }
  } catch (e) {
    // fall through
  }
  return 0;
}
